import React from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Avatar,
  Tooltip,
  IconButton,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloseIcon from "@mui/icons-material/Close";
import useStyles from "../styles";

// Converte o tamanho em bytes para a unidade mais legível (B, KB, MB, GB, TB)
const formatFileSize = (bytes) => {
  if (typeof bytes !== "number" || bytes <= 0) return "";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const unitIndex = Math.min(i, units?.length - 1);
  const size = bytes / Math.pow(1024, unitIndex);

  return `${size?.toFixed(unitIndex === 0 ? 0 : 1)} ${units?.[unitIndex] || ""}`.trim();
};

const FileDropzone = ({
  files = [],
  dragActive = false,
  inputId = "input-upload-circuito",
  accept = "image/*,application/pdf,.doc,.docx,video/*",
  compact = false,
  onDragOver = () => {},
  onDragLeave = () => {},
  onDrop = () => {},
  onFileChange = () => {},
  onRemoveFile = () => {},
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        ...styles.boxContainer,
        border: dragActive
          ? `2px dashed ${theme.palette.primary.main}`
          : `2px dashed ${alpha(theme.palette.text.primary, 0.3)}`,
        ...(files?.length === 0 && {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }),
      }}
    >
      <Typography color="text.secondary" sx={styles.textInfo} align="center">
        {dragActive
          ? "Solte os arquivos aqui"
          : "Arraste, cole (Ctrl+V) ou clique para selecionar"}
      </Typography>

      <input
        type="file"
        hidden
        multiple
        id={inputId}
        onChange={onFileChange}
        accept={accept}
      />

      <label htmlFor={inputId}>
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{ textTransform: "none", mb: 2, mt: 1 }}
        >
          Selecionar arquivos
        </Button>
      </label>

      {files.length > 0 ? (
        <Box sx={{ mt: 1, width: "100%", textAlign: "left" }}>
          {!compact && (
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center" }}>
              <Chip
                label={`${files.length}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  height: 22,
                  fontSize: "0.75rem",
                  borderRadius: "5px",
                }}
              />
            </Box>
          )}

          <Grid container spacing={1.5}>
            {files.map((file, index) => {
              const isImage = file.type?.startsWith("image/");
              const isPdf =
                file.isPdf || file.name?.toLowerCase().endsWith(".pdf");
              const preview = file.preview;
              const sizeBytes =
                typeof file?.size === "number"
                  ? file?.size
                  : typeof file?.originalFile?.size === "number"
                    ? file?.originalFile?.size
                    : 0;
              const sizeLabel = formatFileSize(sizeBytes);

              return (
                <Grid
                  item
                  xs={compact ? 12 : 6}
                  sm={compact ? 12 : 3}
                  md={compact ? 12 : 2}
                  key={index}
                >
                  <Box sx={{ ...styles.boxImagePreview, position: "relative" }}>
                    {isImage ? (
                      <Avatar
                        src={preview}
                        alt={file.name}
                        variant="rounded"
                        sx={styles.avatar}
                      />
                    ) : (
                      <Tooltip title={file?.name || ""}>
                        <Box sx={styles.containerCard}>
                          {isPdf ? (
                            <PictureAsPdfOutlinedIcon
                              sx={{ fontSize: "40px", color: "error.main" }}
                            />
                          ) : (
                            <InsertDriveFileOutlinedIcon
                              sx={{ fontSize: "40px", color: "text.secondary" }}
                            />
                          )}
                          <Box sx={styles.boxTitleAnexo}>
                            <Typography sx={styles.boxTextAnexo} noWrap>
                              {file?.name}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    )}
                    {sizeLabel && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: "center",
                          mt: 0.5,
                          fontSize: "10px",
                          color: "text.secondary",
                        }}
                      >
                        {sizeLabel}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => onRemoveFile(file)}
                      sx={styles.iconButton}
                    >
                      <CloseIcon
                        fontSize="small"
                        sx={{
                          color: theme.palette.common.white,
                          fontSize: "14px",
                        }}
                      />
                    </IconButton>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : (
        <Typography
          color="text.secondary"
          sx={{ ...styles.textInfo, fontSize: "0.8rem", mt: 1 }}
        >
          Nenhum arquivo selecionado
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
